/**
 * The schema an operator pastes into an LLM before asking it for a template.
 *
 * The vocabulary here is not invented for this file. It is taken from
 * templateLibrary/system/elements.js, which is the only place the codebase
 * constructs editor elements and therefore the authority on what the editor
 * actually reads. If the two ever disagree, elements.js is right.
 *
 * The worked example is deliberately complete rather than minimal: a model
 * given a fragment produces fragments, and the operator then spends their
 * afternoon on error lists instead of on the design.
 */

export const SCHEMA_TEXT = `iVisora template JSON
======================

Top level:
{
  "name":              string   required
  "description":       string   optional
  "library_id":        string   optional — supply to update an existing import
                                rather than create a new one
  "canvas_width":      number   optional, defaults to 1080
  "canvas_height":     number   optional, defaults to 1080
  "canvas_background": string   optional, defaults to "#ffffff"
  "tags":              string[] optional
  "is_premium":        boolean  optional
  "pages":             Page[]   required
}

Page:
{
  "id":         string    required, unique
  "name":       string    optional, defaults to "Page N"
  "background": string    optional, defaults to "#ffffff"
  "elements":   Element[] required (may be empty)
}

Every element:
{
  "id":       string  required, unique within the page
  "type":     string  required — one of: text image shape frame sticker group svg
  "x":        number  required     "y":      number required
  "width":    number  required     "height": number required
  "rotation": number  optional, defaults to 0
  "opacity":  number  optional, defaults to 1
  "locked":   boolean optional, defaults to false
  "visible":  boolean optional, defaults to true
  "parentId": string  optional — the id of a group element on the same page
}

type "text" additionally requires text, fontSize, fontFamily, color:
  "text":          string   use \\n for a line break
  "fontSize":      number
  "fontFamily":    string
  "color":         string
  "fontWeight":    string   optional, e.g. "700"
  "textAlign":     string   optional — left | center | right
  "letterSpacing": string   optional, e.g. "2px"
  "lineHeight":    number   optional

type "shape" additionally requires shape and fill:
  "shape":        string  rect roundedRect circle triangle diamond hexagon
                          pentagon octagon star heart arrow cross
                          parallelogram line
  "fill":         string
  "stroke":       string  optional
  "strokeWidth":  number  optional
  "cornerRadius": number  optional

type "image" additionally requires src:
  "src":          string  an https URL, or a {{PLACEHOLDER}} token
  "cornerRadius": number  optional

type "frame" additionally requires shape, image and fit:
  "shape": "rectangle" | "circle"
  "fit":   "cover" | "contain"
  "image": null, or an OBJECT — not a bare URL string:
           { "src": string, "offsetX": number, "offsetY": number,
             "scale": number, "rotation": number }
           Use null for an empty frame the user will drop a photo into.

type "sticker" additionally requires emoji:
  "emoji": string

type "svg" additionally requires svgContent:
  "svgContent": string  raw <svg>...</svg> markup

type "group" carries no geometry — its bounds are derived from its children.
Children reference it by parentId.

NOT IMPORTABLE. The editor renders these, but import rejects them:
  chart  table  qrcode  drawing  penPath

PLACEHOLDERS
Write {{TOKEN}} inside a text element's "text" or an image element's "src" and
it becomes an editable field. Recognised names:
  TITLE SUBTITLE DESCRIPTION PHONE EMAIL WEBSITE PRICE ADDRESS
  IMAGE1 IMAGE2 IMAGE3 LOGO BUTTON CTA
Other names work too, but are flagged so a typo stays visible.`;

export const EXAMPLE_JSON = {
  name: 'Open House — Navy',
  description: 'A single-page real estate flyer with a photo band and a details card.',
  canvas_width: 1080,
  canvas_height: 1350,
  canvas_background: '#0f1b2d',
  tags: ['real estate', 'flyer', 'open house'],
  is_premium: false,
  pages: [
    {
      id: 'page-1',
      name: 'Flyer',
      background: '#0f1b2d',
      elements: [
        {
          id: 'hero',
          type: 'image',
          x: 0, y: 0, width: 1080, height: 620,
          src: '{{IMAGE1}}',
        },
        {
          id: 'card',
          type: 'shape',
          x: 80, y: 540, width: 920, height: 500,
          shape: 'roundedRect',
          fill: '#f7f4ee',
          cornerRadius: 24,
        },
        {
          id: 'eyebrow',
          type: 'text',
          x: 128, y: 596, width: 820, height: 40,
          text: 'OPEN HOUSE',
          fontSize: 26,
          fontFamily: 'Inter',
          fontWeight: '700',
          color: '#b08341',
          letterSpacing: '8px',
          textAlign: 'left',
        },
        {
          id: 'headline',
          type: 'text',
          x: 128, y: 648, width: 820, height: 200,
          text: '{{TITLE}}',
          fontSize: 76,
          fontFamily: 'Playfair Display',
          fontWeight: '700',
          color: '#0f1b2d',
          lineHeight: 1.05,
          textAlign: 'left',
        },
        {
          id: 'price',
          type: 'text',
          x: 128, y: 872, width: 820, height: 70,
          text: '{{PRICE}}',
          fontSize: 54,
          fontFamily: 'Inter',
          fontWeight: '700',
          color: '#0f1b2d',
          textAlign: 'left',
        },
        {
          id: 'address',
          type: 'text',
          x: 128, y: 950, width: 820, height: 44,
          text: '{{ADDRESS}}',
          fontSize: 28,
          fontFamily: 'Inter',
          color: '#5b6472',
          textAlign: 'left',
        },
        {
          id: 'rule',
          type: 'shape',
          x: 128, y: 1010, width: 820, height: 2,
          shape: 'rect',
          fill: '#d8d2c6',
        },
        {
          id: 'contact',
          type: 'text',
          x: 128, y: 1036, width: 820, height: 40,
          text: '{{PHONE}}  ·  {{EMAIL}}',
          fontSize: 24,
          fontFamily: 'Inter',
          color: '#0f1b2d',
          textAlign: 'left',
        },
      ],
    },
  ],
};

/** Schema plus one complete worked example, ready for the clipboard. */
export const schemaForClipboard = () =>
  `${SCHEMA_TEXT}\n\nCOMPLETE EXAMPLE\n================\n\n${JSON.stringify(EXAMPLE_JSON, null, 2)}\n`;
